/**
 * Sticky Position Manager
 * Centralized system for coordinating all sticky elements (header, banners, etc.)
 * Handles dynamic positioning based on scroll state and element visibility
 */

class StickyPositionManager {
  constructor() {
    this.elements = new Map();
    this.scrollState = {
      direction: 'up',
      position: 0,
      lastPosition: 0
    };
    
    this.init();
  }

  init() {
    this.bindEvents();
    this.updateScrollState();
  }

  bindEvents() {
    let ticking = false;
    
    window.addEventListener('scroll', () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.updateScrollState();
          this.updateAllPositions();
          ticking = false;
        });
        ticking = true;
      }
    });

    window.addEventListener('resize', () => {
      this.updateAllPositions();
    });
  }

  /**
   * Register a sticky element with the manager
   * @param {string} id - Unique identifier for the element
   * @param {HTMLElement} element - The DOM element
   * @param {Object} config - Configuration object
   */
  register(id, element, config = {}) {
    const defaultConfig = {
      priority: 0, // Higher number = higher priority (closer to top)
      stickyMode: 'always', // 'always', 'scroll-up', 'manual'
      originalPosition: null,
      isSticky: false,
      height: 0
    };

    this.elements.set(id, {
      element,
      config: { ...defaultConfig, ...config },
      callbacks: {
        onPositionChange: config.onPositionChange || function() {},
        onStickyStateChange: config.onStickyStateChange || function() {}
      }
    });

    // Calculate initial position
    this.updateElementPosition(id);
  }

  /**
   * Unregister an element
   * @param {string} id - Element identifier
   */
  unregister(id) {
    this.elements.delete(id);
    this.updateAllPositions();
  }

  /**
   * Update scroll state tracking
   */
  updateScrollState() {
    const currentPosition = window.scrollY;
    const direction = currentPosition > this.scrollState.lastPosition ? 'down' : 'up';
    
    this.scrollState = {
      direction,
      position: currentPosition,
      lastPosition: this.scrollState.position
    };
  }

  /**
   * Update positions for all registered elements
   */
  updateAllPositions() {
    // Sort elements by priority (highest first)
    const sortedElements = Array.from(this.elements.entries())
      .sort(([,a], [,b]) => b.config.priority - a.config.priority);

    let cumulativeOffset = 0;

    sortedElements.forEach(([id, data]) => {
      const shouldBeSticky = this.shouldElementBeSticky(id);
      const shouldBeVisible = this.shouldElementBeVisible(id);
      
      // Update sticky state
      if (shouldBeSticky !== data.config.isSticky) {
        data.config.isSticky = shouldBeSticky;
        data.callbacks.onStickyStateChange(shouldBeSticky);
      }

      // Calculate position
      let topPosition = 0;
      if (shouldBeSticky && shouldBeVisible) {
        topPosition = cumulativeOffset;
        cumulativeOffset += this.getElementHeight(id);
      } else if (shouldBeSticky && !shouldBeVisible) {
        topPosition = -this.getElementHeight(id);
      }

      // Update element position
      data.callbacks.onPositionChange({
        top: topPosition,
        isSticky: shouldBeSticky,
        isVisible: shouldBeVisible,
        zIndex: 1000 + data.config.priority
      });

      // Update CSS custom properties
      this.updateCSSProperties(id, {
        top: topPosition,
        height: this.getElementHeight(id),
        isVisible: shouldBeVisible
      });
    });
  }

  /**
   * Determine if an element should be sticky
   * @param {string} id - Element identifier
   * @returns {boolean}
   */
  shouldElementBeSticky(id) {
    const data = this.elements.get(id);
    if (!data) return false;

    const { config } = data;
    
    switch (config.stickyMode) {
      case 'always':
        return this.scrollState.position > (config.originalPosition || 0);
      
      case 'scroll-up':
        return this.scrollState.position > (config.originalPosition || 0);
      
      case 'manual':
        return config.isSticky;
      
      default:
        return false;
    }
  }

  /**
   * Determine if a sticky element should be visible
   * @param {string} id - Element identifier
   * @returns {boolean}
   */
  shouldElementBeVisible(id) {
    const data = this.elements.get(id);
    if (!data) return false;

    const { config } = data;
    
    switch (config.stickyMode) {
      case 'always':
        return true;
      
      case 'scroll-up':
        return this.scrollState.direction === 'up' || this.scrollState.position <= 10;
      
      case 'manual':
        return config.isVisible !== false;
      
      default:
        return true;
    }
  }

  /**
   * Get current height of an element
   * @param {string} id - Element identifier
   * @returns {number}
   */
  getElementHeight(id) {
    const data = this.elements.get(id);
    if (!data) return 0;

    const rect = data.element.getBoundingClientRect();
    data.config.height = rect.height;
    return rect.height;
  }

  /**
   * Update element's original position
   * @param {string} id - Element identifier
   */
  updateElementPosition(id) {
    const data = this.elements.get(id);
    if (!data) return;

    const rect = data.element.getBoundingClientRect();
    data.config.originalPosition = rect.top + window.scrollY;
  }

  /**
   * Update CSS custom properties for an element
   * @param {string} id - Element identifier
   * @param {Object} values - Values to set
   */
  updateCSSProperties(id, values) {
    const prefix = `--sticky-${id}`;
    
    document.documentElement.style.setProperty(`${prefix}-top`, `${values.top}px`);
    document.documentElement.style.setProperty(`${prefix}-height`, `${values.height}px`);
    document.documentElement.style.setProperty(`${prefix}-visible`, values.isVisible ? '1' : '0');
  }

  /**
   * Get element data
   * @param {string} id - Element identifier
   * @returns {Object|null}
   */
  getElement(id) {
    return this.elements.get(id) || null;
  }

  /**
   * Update element configuration
   * @param {string} id - Element identifier
   * @param {Object} updates - Configuration updates
   */
  updateConfig(id, updates) {
    const data = this.elements.get(id);
    if (data) {
      Object.assign(data.config, updates);
      this.updateAllPositions();
    }
  }

  /**
   * Force recalculation of all positions
   */
  recalculate() {
    this.elements.forEach((data, id) => {
      this.updateElementPosition(id);
    });
    this.updateAllPositions();
  }
}

// Create global instance
if (!window.stickyPositionManager) {
  window.stickyPositionManager = new StickyPositionManager();
}

export { StickyPositionManager };
